import React from 'react';
import MuiPagination, {
  PaginationProps as MuiPaginationProps,
} from '@material-ui/lab/Pagination';
import PaginationItem from '@material-ui/lab/PaginationItem';
import makeStyles from '@material-ui/core/styles/makeStyles';
import useTheme from '@material-ui/core/styles/useTheme';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import clsx from 'clsx';
import MuiLink from '@material-ui/core/Link';
import { useLocation } from 'react-router-dom';
import RouterLink from '../Link/RouterLink';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    justifyContent: 'center',
  },
  itemRoot: {
    color: theme.palette.primary.main,
    fontWeight: 600,
    '&:hover': {
      textDecoration: 'none',
    },
    [theme.breakpoints.up('sm')]: {
      margin: '0 5px',
    },
  },
  selected: {
    color: theme.palette.common.white,
    backgroundColor: '#432511 !important',
  },
  outlined: {
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.text.secondary}`,
  },
}));

export type PaginationProps = Omit<
  MuiPaginationProps,
  'siblingCount',
  'renderItem',
> & {
  router?: boolean,
};

const Pagination = ({
  page: pageProps,
  onChange,
  className,
  router,
  ...otherPaginationProps
}: PaginationProps) => {
  let location;
  try {
    const routerLocation = useLocation();
    location = router ? routerLocation : window.location;
  } catch (error) {
    location = window.location;
  }

  const getSearchParams = () => new URLSearchParams(location.search);

  const getCurrentPage = (page) =>
    page >= 0
      ? page
      : Math.max(parseInt(getSearchParams().get('page'), 10) || 1, 1);

  const [page, setPage] = React.useState(getCurrentPage(pageProps));

  const classes = useStyles();

  const theme = useTheme();

  const matches = useMediaQuery(theme.breakpoints.up('sm'));

  const getItemProps = (page) => {
    const params = getSearchParams();
    page > 1 ? params.set('page', page) : params.delete('page');
    const query = params.toString();
    const path = location.pathname + (query ? '?' + query : '');
    if (router) {
      return { component: RouterLink, to: path };
    } else {
      return { component: MuiLink, href: path };
    }
  };

  const handleChange = (event, nextPage) => {
    if (onChange && page !== nextPage) {
      setPage(nextPage);
      onChange(event, nextPage);
    }
  };

  React.useEffect(() => {
    if (!onChange) {
      // Change page when page prop changes
      const nextPage = getCurrentPage(pageProps);
      page !== nextPage && setPage(nextPage);
    }
  }, [pageProps]);

  return (
    <MuiPagination
      shape="rounded"
      count={1}
      boundaryCount={1}
      {...otherPaginationProps}
      className={clsx(classes.root, className)}
      page={page}
      siblingCount={matches ? 1 : 0}
      onChange={handleChange}
      renderItem={(item) => (
        <PaginationItem
          {...item}
          {...(!onChange && item.page !== page && getItemProps(item.page))}
          variant={
            item.type === 'page' && !item.selected ? 'outlined' : undefined
          }
          classes={{
            root: classes.itemRoot,
            selected: classes.selected,
            outlined: classes.outlined,
          }}
        />
      )}
    />
  );
};

export default Pagination;
